import { Table } from 'antd';
import { forwardRef, useEffect, useState } from 'react';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { frenchMonth } from '../../../../variables/functions';

const BordereauEmissions = forwardRef(({
    data,
    columns,
    selectedRows,
    onSelectionChange,
    onValidate,
    onPrint,
    isValidated,
    isDisabled,
    loading,
    categorie,
    periode,
    compagnie
}, ref) => {

    const [isPrinting, setIsPrinting] = useState(false);

    // Select all rows by default when data changes and no selection exists
    useEffect(() => {
        if (data.length > 0 && selectedRows.length === 0 && !isValidated && !isDisabled) {
            onSelectionChange(data); // all rows selected initially
        }
    }, [data, selectedRows, onSelectionChange, isValidated, isDisabled]);

    const rowSelection = {
        selectedRowKeys: selectedRows.map(r => r.key),
        onChange: (selectedRowKeys, selectedRows) => {
            onSelectionChange(selectedRows);
        },
        getCheckboxProps: () => ({
            disabled: isValidated || isDisabled,
        }),
    };

    const handlePrint = () => {
        setIsPrinting(true);
        // Petit délai pour s'assurer que le DOM est mis à jour
        setTimeout(() => {
            onPrint();
            // Remettre l'état après l'impression
            setTimeout(() => setIsPrinting(false), 100);
        }, 100);
    };

    return (
        <div>
            {/* Version affichage */}
            <div className="mb-4" style={{ padding: 15 }}>
                <Table
                    rowKey="codeId"
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={data}
                    pagination={{ showSizeChanger: true }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={6}>
                                    <strong>TOTAL</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    <strong>{displayNumbers(data?.reduce(
                                        (sum, item) =>
                                            sum + ((selectedRows.find(r => r.key === item.key)) ? (item.primeACeder || 0) : 0),
                                        0
                                    ) || 0)} F CFA</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                <div className="mt-3 d-flex gap-2">
                    <button
                        type="button"
                        onClick={onValidate}
                        disabled={isDisabled || isValidated || loading}
                        className={isValidated ? "btn btn-success" : "btn btn-primary"}
                    >
                        {loading && (
                            <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                        )}
                        <i className="tf-icons bx bxs-badge-check bx-18px me-2"></i>
                        <span className="align-middle">
                            {loading ? 'Validation...' : isValidated
                                ? `Émissions ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode?.split("-")?.[0] : ''} validées`
                                : `Valider les émissions ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode?.split("-")?.[0] : ''}`
                            }
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={handlePrint}
                        className="btn btn-label-pinterest"
                    >
                        <i className="tf-icons bx bxs-file-pdf bx-18px me-2"></i>
                        Imprimer
                    </button>
                </div>
            </div>

            {/* Version impression (cachée) */}
            <div ref={ref} style={{
                position: isPrinting ? 'relative' : 'absolute',
                left: isPrinting ? 'auto' : '-9999px',
                visibility: isPrinting ? 'visible' : 'hidden'
            }}>
                <style>
                    {`
                        @media print {
                            @page {
                                size: landscape;
                                margin: 20mm;
                            }
                            body {
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                        }
                    `}
                </style>
                <div style={{ padding: '40px', fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                            BORDEREAU DES EMISSIONS DU MOIS DE {periode ? frenchMonth(periode.split("-")[1]).toUpperCase() + " " + periode.split("-")[0] : ''}
                        </h2>
                        <h3 style={{ margin: '10px 0', fontSize: '16px' }}>
                            Catégorie {categorie?.codeCategorie}
                        </h3>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            Compagnie: {compagnie?.raisonSociale}
                        </p>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>N° Police</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Avenant</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Assuré</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Effet</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Échéance</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Catégorie</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>PN Pool</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={index}>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.numeroPolice}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.avenant}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.assure}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{new Date(item.effet).toLocaleDateString()}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{new Date(item.echeance).toLocaleDateString()}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.categorie}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{displayNumbers(item.primeACeder)}</td>
                                </tr>
                            ))}
                            <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                                <td colSpan={6} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>TOTAL</td>
                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{displayNumbers(data?.reduce(
                                    (sum, item) =>
                                        sum + ((selectedRows.find(r => r.key === item.key)) ? (item.primeACeder || 0) : 0),
                                    0
                                ) || 0)} F CFA</td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <p style={{ margin: 0 }}>Date: {new Date().toLocaleDateString()}</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ margin: 0, marginBottom: '60px' }}>Cachet et Signature</p>
                            <p style={{ margin: 0, borderTop: '1px solid #000', paddingTop: '5px', width: '200px' }}>
                                {compagnie?.raisonSociale}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

BordereauEmissions.displayName = 'BordereauEmissions';

export default BordereauEmissions;